/**
 * TrophyCase — gallery of Certified Scholar Badges. Earned badges render as
 * high-end SVG medals (royal blue / gold / black brand tones); locked ones are dim.
 * Each earned badge has a 1-click "Share" that generates a PNG for recruiting/social.
 */
import React, { useEffect, useState } from 'react';
import { Award, Flame, BarChart3, Users, Lock, Share2, Loader2, Globe, Copy, ExternalLink } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const ICONS = { award: Award, flame: Flame, 'bar-chart-3': BarChart3, users: Users };
const TONES = {
  gold: { ring: '#F5B301', bg: '#3a2c00', text: '#F5B301' },
  royal: { ring: '#2447D8', bg: '#0a1636', text: '#6b8cff' },
  black: { ring: '#e4e4e7', bg: '#18181b', text: '#e4e4e7' },
};

const shareBadge = (badge) => {
  const tone = TONES[badge.tone] || TONES.royal;
  const c = document.createElement('canvas'); c.width = 700; c.height = 700;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#0a0a0a'; ctx.fillRect(0, 0, 700, 700);
  ctx.beginPath(); ctx.arc(350, 300, 170, 0, Math.PI * 2); ctx.fillStyle = tone.bg; ctx.fill();
  ctx.lineWidth = 14; ctx.strokeStyle = tone.ring; ctx.stroke();
  ctx.fillStyle = tone.text; ctx.font = '900 120px Arial'; ctx.textAlign = 'center'; ctx.fillText('★', 350, 345);
  ctx.fillStyle = '#ffffff'; ctx.font = '900 40px Arial'; ctx.fillText(badge.name.toUpperCase(), 350, 540);
  ctx.fillStyle = '#a1a1aa'; ctx.font = '600 22px Arial'; ctx.fillText('CERTIFIED · SCHOLAR ATHLETE ACADEMY', 350, 590);
  const a = document.createElement('a'); a.href = c.toDataURL('image/png'); a.download = `SAA_Badge_${badge.key}.png`;
  document.body.appendChild(a); a.click(); a.remove();
  toast.success('Badge image downloaded');
};

const TrophyCase = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portfolio, setPortfolio] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/school/badges', { headers: authHeaders() }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    api.get('/academy/portfolio/me', { headers: authHeaders() }).then((r) => setPortfolio(r.data.portfolio)).catch(() => {});
  }, []);

  const publicUrl = portfolio?.slug ? `${window.location.origin}/academy/portfolio/${portfolio.slug}` : '';

  const togglepublic = async () => {
    setSaving(true);
    try {
      const next = !portfolio?.public;
      const r = await api.post('/academy/portfolio/visibility', { public: next }, { headers: authHeaders() });
      setPortfolio((p) => ({ ...(p || {}), public: r.data.public, slug: r.data.slug }));
      toast.success(next ? 'Portfolio is now public — share your link!' : 'Portfolio set to private');
    } catch { toast.error('Could not update sharing'); } finally { setSaving(false); }
  };

  const copyLink = () => { navigator.clipboard.writeText(publicUrl); toast.success('Public link copied'); };

  return (
    <div className="mb-6 border-2 border-yellow-500/30 p-5" data-testid="trophy-case">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Award className="w-4 h-4 text-yellow-400" /><h3 className="text-sm font-black tracking-[0.2em] uppercase">Scholar-Athlete Digital Portfolio</h3></div>
        {data && <span className="text-[11px] font-black text-zinc-500" data-testid="trophy-count">{data.earned_count}/{data.total} Earned</span>}
      </div>

      {/* Public sharing bar */}
      <div className="mb-5 border-2 border-zinc-800 bg-black/40 p-4" data-testid="portfolio-share-bar">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Globe className={`w-4 h-4 ${portfolio?.public ? 'text-emerald-400' : 'text-zinc-600'}`} />
            <div>
              <div className="text-xs font-black text-white uppercase tracking-wider">Public Portfolio {portfolio?.public ? '· Live' : '· Private'}</div>
              <div className="text-[10px] text-zinc-500">Shareable with schools, coaches & college recruiters — verified capstones + CA standard mastery.</div>
            </div>
          </div>
          <button onClick={togglepublic} disabled={saving} role="switch" aria-checked={!!portfolio?.public}
            className={`relative w-12 h-6 rounded-full transition-colors ${portfolio?.public ? 'bg-emerald-500' : 'bg-zinc-700'}`} data-testid="portfolio-visibility-toggle">
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${portfolio?.public ? 'translate-x-6' : ''}`} />
          </button>
        </div>
        {portfolio?.public && publicUrl && (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <input readOnly value={publicUrl} className="flex-1 min-w-[180px] bg-zinc-950 border border-zinc-800 px-3 py-2 text-[11px] text-zinc-300 font-mono" data-testid="portfolio-public-url" />
            <button onClick={copyLink} className="flex items-center gap-1 border-2 border-zinc-700 hover:border-white text-white px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-colors" data-testid="portfolio-copy-link"><Copy className="w-3.5 h-3.5" /> Copy</button>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 bg-yellow-500 hover:bg-yellow-400 text-black px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-colors" data-testid="portfolio-view-public"><ExternalLink className="w-3.5 h-3.5" /> View</a>
          </div>
        )}
      </div>
      {loading ? (
        <div className="h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-yellow-400" /></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(data?.badges || []).map((b) => {
            const Icon = ICONS[b.icon] || Award;
            const tone = TONES[b.tone] || TONES.royal;
            return (
              <div key={b.key} className={`border-2 p-4 text-center transition-all ${b.earned ? 'border-zinc-700 bg-zinc-900' : 'border-zinc-900 opacity-50'}`} data-testid={`badge-${b.key}`}>
                <div className="relative w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ background: b.earned ? tone.bg : '#111', border: `3px solid ${b.earned ? tone.ring : '#3f3f46'}` }}>
                  {b.earned ? <Icon className="w-7 h-7" style={{ color: tone.text }} /> : <Lock className="w-6 h-6 text-zinc-600" />}
                </div>
                <div className="text-[11px] font-black text-white mt-2 leading-tight uppercase">{b.name}</div>
                <p className="text-[9px] text-zinc-500 mt-1 leading-snug">{b.desc}</p>
                {b.earned && (
                  <button onClick={() => shareBadge(b)} className="mt-2 inline-flex items-center gap-1 text-[9px] font-black tracking-widest uppercase text-yellow-400 hover:text-yellow-300" data-testid={`share-badge-${b.key}`}>
                    <Share2 className="w-3 h-3" /> Share
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TrophyCase;
